"use client";

import KibbeFrameworkNote from "@/components/kibbe/KibbeFrameworkNote";

import { Link } from "@/i18n/navigation";
import { trackCtaClick } from "@/lib/analytics";
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Paper,
  Box,
  Divider,
  Accordion,
  Anchor,
  ThemeIcon,
  SimpleGrid,
  Badge,
  Image,
  Card,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconX, IconSparkles, IconCamera } from "@tabler/icons-react";
import { kibbeTypes, kibbeAccentColors } from "../kibbe/kibbeData";
import { kibbeExtended } from "../kibbe/kibbeExtendedData";

type Faq = { question: string; answer: string };

const MISTYPE_TRAPS = [
  {
    wrong: "“I have a full bust, so I must be Romantic”",
    right: "Romantic means delicate bones under the curves — small wrists, sloped narrow shoulders, short vertical. A full bust on a wide or long frame points to Soft Natural, Soft Dramatic, or Flamboyant Natural instead.",
  },
  {
    wrong: "“I'm plus size, so the angular types are out”",
    right: "Dramatics and Dramatic Classics keep their angularity at every size. If your jaw stays sharp and your silhouette stays comparatively straight as you gain, that's evidence for a yang type, not against it.",
  },
  {
    wrong: "“My body is round now, so my type changed”",
    right: "Kibbe himself is explicit on this: your image identity never changes. Bones don't move. What worked on your frame before works now — usually executed in more generous cuts and better fabric.",
  },
  {
    wrong: "“Plus size means hiding in loose black layers”",
    right: "Shapeless coverage is the one strategy that fails every single type. Each of the 13 types has a specific plus size formula — drape for Romantics, sweep for Naturals, structure for Classics, definition for Gamines.",
  },
];

export default function PlusSizeKibbeContent({ faqs }: { faqs: Faq[] }) {
  const typeEntries = Object.entries(kibbeTypes);

  return (
    <Container size="xl" py={48}>
      <Stack gap={48}>
        {/* Hero */}
        <Box>
          <Badge size="xl" variant="light" color="grape" mb={16}>
            Plus Size Style Guide
          </Badge>
          <Title order={1} fz={{ base: 32, md: 44 }} fw={300} mb={16} style={{ letterSpacing: -1 }}>
            Plus Size Kibbe Body Types
          </Title>
          <Text fz="xl" c="dimmed" maw={720} style={{ lineHeight: 1.7 }}>
            Here is the sentence that should be at the top of every Kibbe article and almost never is:
            your weight has nothing to do with your Kibbe type. Bone structure decides your type, and
            bones don&apos;t change size. What changes at higher weights is how your type <em>presents</em> —
            and that&apos;s exactly what this guide covers, type by type.
          </Text>
        </Box>
        <KibbeFrameworkNote />

        {/* Why weight doesn't change type */}
        <Paper p={{ base: "lg", md: "xl" }} radius="lg" withBorder>
          <Title order={2} fz={24} fw={400} mb={16}>
            Why Weight Doesn&apos;t Change Your Kibbe Type
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
            <Text fz="md" c="dimmed" style={{ lineHeight: 1.8 }}>
              Kibbe&apos;s system reads three things: your vertical line (how long your body appears), your
              bone structure (sharp, blunt, or delicate), and how flesh sits over that structure. Only the
              third one responds to weight — and it&apos;s the least important of the three for determining
              type. A wide, blunt shoulder line is wide and blunt at any size. A 5&apos;1&quot; frame never
              becomes statuesque and a 5&apos;9&quot; one never becomes petite.
              {" "}That&apos;s why the reliable typing signals for plus size women are the ones weight can&apos;t
              touch: height, shoulder-to-hip ratio, wrist and ankle scale, jawline geometry, and hand size.
            </Text>
            <Image
              src="/kibbe/guide/yin-yang-spectrum.png"
              alt="Kibbe yin yang spectrum across all 13 body types"
              radius="md"
              loading="lazy"
            />
          </SimpleGrid>
        </Paper>

        {/* Mistype traps */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={8}>
            The Four Plus Size Mistyping Traps
          </Title>
          <Text fz="sm" c="dimmed" mb={24}>
            Most plus size women get funneled into Romantic or Soft Natural by quizzes that read curves as
            the whole story. These are the corrections.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={16}>
            {MISTYPE_TRAPS.map((trap, i) => (
              <Paper key={i} p="lg" radius="md" withBorder>
                <Group gap={10} mb={8} wrap="nowrap" align="flex-start">
                  <ThemeIcon color="red" size={28} radius="xl" variant="light">
                    <IconX size={16} />
                  </ThemeIcon>
                  <Text fw={600} fz="sm">{trap.wrong}</Text>
                </Group>
                <Group gap={10} wrap="nowrap" align="flex-start">
                  <ThemeIcon color="green" size={28} radius="xl" variant="light">
                    <IconCheck size={16} />
                  </ThemeIcon>
                  <Text fz="sm" c="dimmed" style={{ lineHeight: 1.6 }}>{trap.right}</Text>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        </Box>

        <Divider />

        {/* All 13 types */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={8}>
            All 13 Kibbe Types at Plus Sizes
          </Title>
          <Text fz="sm" c="dimmed" mb={24}>
            How each type tends to carry weight, and the one styling rule that matters most. Each link goes
            to the full type guide with a dedicated plus size section.
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={16}>
            {typeEntries.map(([id, type]) => {
              const extended = kibbeExtended[id];
              const accent = kibbeAccentColors[id];
              if (!extended) return null;
              return (
                <Card key={id} padding="lg" radius="md" withBorder>
                  <Group justify="space-between" mb={8}>
                    <Text fw={600}>{type.name}</Text>
                    <Badge variant="light" color={accent?.primary.split(".")[0] || "gray"} size="sm">
                      {type.family}
                    </Badge>
                  </Group>
                  <Text fz="sm" c="dimmed" style={{ lineHeight: 1.6 }} mb={12}>
                    {extended.plusSize.intro}
                  </Text>
                  <Anchor component={Link} href={`/kibbe/${id}`} fz="sm">
                    Full {type.name} guide (incl. plus size styling) →
                  </Anchor>
                </Card>
              );
            })}
          </SimpleGrid>
        </Box>

        <Divider />

        {/* CTA */}
        <Paper
          p={{ base: "xl", md: 48 }}
          radius="lg"
          ta="center"
          style={{
            background: "linear-gradient(135deg, #862e9c 0%, #e64980 100%)",
          }}
        >
          <ThemeIcon size={56} radius="xl" variant="light" mb={16} style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
            <IconCamera size={30} />
          </ThemeIcon>
          <Title order={2} fz={{ base: 24, md: 32 }} fw={400} mb={12} c="white">
            Find Your Type From a Photo — At Any Size
          </Title>
          <Text fz="md" mb={32} maw={560} mx="auto" style={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.7 }}>
            Self-typing is hardest exactly when quizzes keep reading your curves instead of your bones. Our
            AI analyzes proportions, vertical line, and bone structure directly from your photo — the parts
            of you that don&apos;t change with weight.
          </Text>
          <Button
            component={Link}
            href="/kibbe-body-type-test"
            onClick={() => trackCtaClick("kibbe_plus_test_inline")}
            size="xl"
            rightSection={<IconArrowRight size={20} />}
            styles={{ root: { background: "white", color: "#862e9c", fontWeight: 600 } }}
          >
            Take the Free Kibbe Test
          </Button>
        </Paper>

        {/* FAQ */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            Plus Size Kibbe FAQ
          </Title>
          <Accordion variant="separated">
            {faqs.map((faq, i) => (
              <Accordion.Item key={i} value={`faq-${i}`}>
                <Accordion.Control>
                  <Text fw={500}>{faq.question}</Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text fz="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                    {faq.answer}
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Box>

        {/* Related */}
        <Box>
          <Title order={3} fz={18} fw={500} mb={16}>
            Keep Going
          </Title>
          <Group gap={12}>
            <Button
              component={Link}
              href="/kibbe-body-type-test"
              onClick={() => trackCtaClick("kibbe_plus_test_bottom")}
              variant="default"
              size="sm"
            >
              Kibbe Body Type Test
            </Button>
            <Button component={Link} href="/kibbe-body-types-guide" variant="default" size="sm">
              Current & Historical Types Guide
            </Button>
            <Button component={Link} href="/kibbe-outfits" variant="default" size="sm">
              Kibbe Outfit Ideas
            </Button>
            <Button component={Link} href="/how-to-dress-for-your-body-type" variant="default" size="sm">
              How to Dress for Your Body Type
            </Button>
          </Group>
        </Box>
      </Stack>
    </Container>
  );
}
